// Project detail — workflow lifecycle tracker, stage management, quick actions
// and related records (site visits, inspections, documents, actions).
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowLeft, Loader2, CheckCircle2, Circle, AlertTriangle, Clock, ChevronRight,
    CalendarPlus, ClipboardCheck, Upload, ListPlus, FileText, BadgeCheck, Send, HardHat,
    Globe2, Archive, User, CalendarDays, TrendingUp, Plus, Trash2, X,
    Users, ShieldCheck, Pencil, Zap, ChevronDown, LayoutDashboard, Truck, PencilRuler, Construction, Wrench, History,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import Pill from "@/components/Pill";
import GanttTimeline from "@/components/GanttTimeline";
import ComplianceHub from "@/components/ComplianceHub";
import ProjectSitesPanel from "@/components/ProjectSitesPanel";
import ProjectSubcontractorsPanel from "@/components/ProjectSubcontractorsPanel";
import ProjectWorksPanel from "@/components/ProjectWorksPanel";
import RequiredAuditsPanel from "@/components/RequiredAuditsPanel";
import { SectionCard } from "@/components/SectionCard";
import PortalComplianceHub from "@/components/PortalComplianceHub";
import DocumentBrowser from "@/components/documents/DocumentBrowser";
import ProjectInductionRecords from "@/components/ProjectInductionRecords";
import UnifiedCalendar from "@/components/calendar/UnifiedCalendar";
import PortalProjectView from "@/pages/projects/PortalProjectView";
import EquipmentOnSitePanel from "@/components/equipment/EquipmentOnSitePanel";
import { ProjectModuleToggles } from "@/components/ProjectModuleToggles";
import SiteQRPoster from "@/components/SiteQRPoster";
import ProjectContactsEditor from "@/components/ProjectContactsEditor";
import AddRecordDialog from "@/components/AddRecordDialog";
import CdmRollup from "@/components/CdmRollup";
import ProjectTimeSummary from "@/components/ProjectTimeSummary";import { ProjectDialog, STATUS_TONE } from "@/pages/ProjectsPage";
import ProjectRamsPanel from "@/components/rams/ProjectRamsPanel";
import ProjectCppPanel from "@/components/cpp/ProjectCppPanel";

const CAN_EDIT = new Set(["director", "administrator", "consultant"]);
const STAGE_TONE = { "Not started": "neutral", "In progress": "blue", "Blocked": "red", "Complete": "green" };
const STAGE_STATUSES = ["Not started", "In progress", "Blocked", "Complete"];
const TASK_STATUSES = ["Not Started", "In Progress", "Blocked", "Complete", "Not Applicable"];
const TASK_TONE = { "Not Started": "neutral", "In Progress": "blue", "Blocked": "red", "Complete": "green", "Not Applicable": "neutral" };
const HEALTH = {
    red: { label: "At risk", bg: "#FBE8E5", fg: "#B33A2E" },
    amber: { label: "Watch", bg: "#FCF1DC", fg: "#C68A1C" },
    green: { label: "On track", bg: "#E8F1E6", fg: "#3F7D3A" },
};

function stageIcon(status) {
    if (status === "Complete") return <CheckCircle2 className="w-4 h-4" />;
    if (status === "Blocked") return <AlertTriangle className="w-4 h-4" />;
    if (status === "In progress") return <Clock className="w-4 h-4" />;
    return <Circle className="w-4 h-4" />;
}
const stageColor = (s) => ({ "Complete": "#2E7D4F", "Blocked": "#B33A2E", "In progress": "#2A5DB0", "Not started": "#B8B2A8" }[s] || "#B8B2A8");

// Due/late status for a task's linked action
function dueInfo(action) {
    if (action.status === "Complete") return { label: "Completed", bg: "#E8F1E6", fg: "#3F7D3A" };
    if (!action.due_date) return { label: "No due date", bg: "#F0EDE8", fg: "#3e4b63" };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(action.due_date + "T00:00:00");
    const diff = Math.round((due - today) / 86400000);
    if (diff < 0) return { label: `${-diff} day${-diff === 1 ? "" : "s"} overdue`, bg: "#FBE8E5", fg: "#B33A2E" };
    if (diff === 0) return { label: "Due today", bg: "#FCF1DC", fg: "#C68A1C" };
    if (diff <= 3) return { label: `Due in ${diff} day${diff === 1 ? "" : "s"}`, bg: "#FCF1DC", fg: "#C68A1C" };
    return { label: `Due in ${diff} days`, bg: "#EAF1FB", fg: "#2A5DB0" };
}

export default function ProjectDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { isInternalView } = useView();
    const canEdit = user && CAN_EDIT.has(user.role) && isInternalView;
    const [project, setProject] = useState(null);
    const [related, setRelated] = useState(null);
    const [compliance, setCompliance] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTabState] = useState(searchParams.get("tab") || "overview");
    const [accessRefreshKey, setAccessRefreshKey] = useState(0);
    const setActiveTab = (t) => {
        setActiveTabState(t);
        const next = new URLSearchParams(searchParams);
        if (t === "overview") next.delete("tab"); else next.set("tab", t);
        setSearchParams(next, { replace: true });
    };
    const [client, setClient] = useState(null);
    const [activeStage, setActiveStage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [newTask, setNewTask] = useState("");
    const [taskAction, setTaskAction] = useState(null);  // {stageKey, task}
    const [recordDialog, setRecordDialog] = useState(null);  // 'action'|'visit'|'inspection'|'document'
    const [recordPreset, setRecordPreset] = useState("");    // preset inspection template code (e.g. CDM)
    const [editOpen, setEditOpen] = useState(false);
    const [quickOpen, setQuickOpen] = useState(false);
    const quickRef = useRef(null);

    useEffect(() => {
        if (!quickOpen) return;
        const onDown = (e) => { if (quickRef.current && !quickRef.current.contains(e.target)) setQuickOpen(false); };
        const onEsc = (e) => { if (e.key === "Escape") setQuickOpen(false); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onEsc);
        return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onEsc); };
    }, [quickOpen]);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/projects/${id}`);
            setProject(data);
            setActiveStage((prev) => prev || data.workflow_stage);
            api.get(`/projects/${id}/related`).then((r) => setRelated(r.data)).catch(() => setRelated(null));
            api.get(`/projects/${id}/compliance`).then((r) => setCompliance(r.data)).catch(() => setCompliance(null));
            api.get(`/clients/${data.client_id}`).then((r) => setClient(r.data)).catch(() => {});
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load project");
            navigate("/app/projects");
        } finally { setLoading(false); }
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="py-20 text-center text-[#64718a]"><Loader2 className="inline w-5 h-5 animate-spin mr-2" />Loading project…</div>;
    if (!project) return null;

    // Phase 4/5 — portal roles AND internal users previewing in portal view get
    // the lean client project home; only true internal view gets the control centre.
    const isInternal = user && CAN_EDIT.has(user.role) && isInternalView;
    if (!isInternal) {
        return <PortalProjectView project={project} compliance={compliance} related={related} client={client} onReload={load} />;
    }

    const stages = project.stages || [];
    const stage = stages.find((s) => s.key === activeStage) || stages[0];
    const curIdx = stages.findIndex((s) => s.key === project.workflow_stage);
    const actionsById = {};
    (related?.actions || []).forEach((a) => { actionsById[a.id] = a; });

    async function patchStage(stageKey, body) {
        setBusy(true);
        try { const { data } = await api.patch(`/projects/${id}/stages/${stageKey}`, body); setProject(data); return true; }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); return false; }
        finally { setBusy(false); }
    }
    async function toggleChecklist(stageKey, label, done) {
        setBusy(true);
        try { const { data } = await api.patch(`/projects/${id}/checklist`, { stage_key: stageKey, label, done }); setProject(data); }
        catch (e) { toast.error("Update failed"); }
        finally { setBusy(false); }
    }
    async function patchTask(stageKey, taskId, body) {
        setBusy(true);
        try { const { data } = await api.patch(`/projects/${id}/stages/${stageKey}/tasks/${taskId}`, body); setProject(data); return true; }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); return false; }
        finally { setBusy(false); }
    }
    async function addTask(stageKey) {
        const label = newTask.trim();
        if (!label) return;
        setBusy(true);
        try { const { data } = await api.post(`/projects/${id}/stages/${stageKey}/tasks`, { label }); setProject(data); setNewTask(""); toast.success("Task added"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add task"); }
        finally { setBusy(false); }
    }
    async function removeTask(stageKey, task) {
        if (!window.confirm(`Remove task "${task.label}"?`)) return;
        setBusy(true);
        try { const { data } = await api.post(`/projects/${id}/stages/${stageKey}/remove-task`, { task_id: task.id, label: task.label }); setProject(data); toast.success("Task removed"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not remove task"); }
        finally { setBusy(false); }
    }
    async function advance() {
        setBusy(true);
        try {
            const { data } = await api.post(`/projects/${id}/advance`);
            setProject(data.project); setActiveStage(data.project.workflow_stage);
            if (data.warnings?.length) toast.warning(`Advanced — but ${data.warnings.length} item(s) were still outstanding: ${data.warnings.join(", ")}`);
            else toast.success("Advanced to next stage");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Cannot advance"); }
        finally { setBusy(false); }
    }
    async function sendToQa() {
        await patchStage("qa", { status: "In progress" });
        setActiveStage("qa"); toast.success("Sent to internal QA");
    }
    async function publish() {
        setBusy(true);
        try { const { data } = await api.post(`/projects/${id}/publish`, { published: !project.published_to_portal }); setProject(data); toast.success(data.published_to_portal ? "Published to client portal" : "Unpublished from portal"); }
        catch (e) { toast.error("Failed"); }
        finally { setBusy(false); }
    }
    async function archive() {
        if (!window.confirm("Archive this project? It will be marked Cancelled and removed from active lists.")) return;
        try { await api.delete(`/projects/${id}`); toast.success("Project archived"); navigate("/app/projects"); }
        catch (e) { toast.error("Failed"); }
    }

    const goNew = (path) => navigate(`${path}?new=1&project=${id}&client=${project.client_id}&project_label=${encodeURIComponent(project.name)}&back=${encodeURIComponent("/app/projects/" + id)}`);

    const openClp = async () => {
        const back = `?back=${encodeURIComponent("/app/projects/" + id)}`;
        try {
            const { data } = await api.get(`/clp?project_id=${id}`);
            if (data && data.length) { navigate(`/app/clp/${data[0].id}${back}`); return; }
            const { data: rec } = await api.post("/clp", {
                client_id: project.client_id, project_id: id,
                building_id: (project.building_ids || [])[0] || null,
                development_name: project.name,
            });
            toast.success("Construction Logistics Plan created");
            navigate(`/app/clp/${rec.id}${back}`);
        } catch (e) { toast.error("Could not open CLP"); }
    };

    const backParam = `&back=${encodeURIComponent("/app/projects/" + id)}`;

    return (
        <div data-testid="project-detail-page" className="space-y-5 pb-10">
            <button onClick={() => navigate("/app/projects")} data-testid="project-back"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: "#3e4b63" }}>
                <ArrowLeft className="w-3.5 h-3.5" /> Back to projects
            </button>

            {/* Header */}
            <div className="bg-white border rounded-[13px] shadow-brand-sm p-5" style={{ borderColor: "#E5E0DA" }}>
                <div className="flex items-start gap-4 flex-wrap">
                    <BuildingThumb />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-2xl font-bold tracking-tight text-[#1F1F1F]">{project.name}</h2>
                            <Pill tone={STATUS_TONE[project.status] || "neutral"}>{project.status}</Pill>
                            {project.published_to_portal && <Pill tone="green">Published</Pill>}
                            {project.health && (
                                <span data-testid="project-health-badge" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
                                    style={{ background: HEALTH[project.health].bg, color: HEALTH[project.health].fg }}>
                                    <span className="w-2 h-2 rounded-full" style={{ background: HEALTH[project.health].fg }} />
                                    {HEALTH[project.health].label}
                                </span>
                            )}
                        </div>
                        <div className="text-[11.5px] font-mono mt-1" style={{ color: "#64718a" }}>
                            {project.reference}{(project.project_types || []).length ? ` · ${project.project_types.join(", ")}` : (project.project_type ? ` · ${project.project_type}` : "")}{project.f10_number ? ` · F10 ${project.f10_number}` : ""}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-[12px]" style={{ color: "#3e4b63" }}>
                            {client && <span className="inline-flex items-center gap-1.5"><span className="w-4 h-4 rounded-[3px] grid place-items-center text-[8px] font-bold text-white" style={{ background: client.branding?.primary_colour }}>{client.branding?.logo_initials}</span>{client.name}</span>}
                            {project.lead_consultant && <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" />{project.lead_consultant}</span>}
                            {project.target_date && <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />Target {project.target_date}</span>}
                            {project.value_gbp != null && <span className="font-mono font-semibold text-[#1F1F1F]">£{project.value_gbp.toLocaleString()}</span>}
                        </div>
                        {project.portal_last_edited_at && (
                            <div data-testid="project-portal-last-edited" className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: "#EFF3EE", color: "#2F6B3C" }}>
                                <Pencil className="w-3 h-3" /> Last edited by {project.portal_last_edited_by_name || "Client Admin"} (Client Admin) on {project.portal_last_edited_at.slice(0, 10)}
                            </div>
                        )}
                    </div>
                    {canEdit && (
                        <div className="flex items-center gap-2 flex-shrink-0 relative" ref={quickRef}>
                            <button data-testid="project-generate-report" onClick={() => navigate(`/app/ai-report?project=${id}&client=${project.client_id}${(project.building_ids || [])[0] ? `&building=${project.building_ids[0]}` : ""}&title=${encodeURIComponent((project.name || "") + " — Compliance report")}`)}
                                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[10px] text-[12.5px] font-semibold text-white"
                                style={{ background: "#1f2a44" }}>
                                <FileText className="w-4 h-4" /> Generate report
                            </button>
                            <button data-testid="project-quick-actions-trigger" onClick={() => setQuickOpen((o) => !o)}
                                aria-expanded={quickOpen} aria-haspopup="menu"
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] text-[12.5px] font-semibold border bg-white hover:bg-[#F6F4F0]"
                                style={{ borderColor: "#E5E0DA", color: "#1f2a44" }}>
                                <Zap className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} /> Quick actions
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${quickOpen ? "rotate-180" : ""}`} style={{ color: "#64718a" }} />
                            </button>
                            {quickOpen && (
                                <div data-testid="project-quick-actions-menu" role="menu"
                                    className="absolute right-0 top-[calc(100%+6px)] z-40 w-60 bg-white border rounded-[12px] shadow-brand-lg py-1.5"
                                    style={{ borderColor: "#E5E0DA" }}>
                                    <QAItem icon={CalendarPlus} label="Schedule site visit" testId="qa-schedule" onClick={() => { setQuickOpen(false); goNew("/app/schedule"); }} />
                                    <QAItem icon={ClipboardCheck} label="Start audit" testId="qa-audit" onClick={() => { setQuickOpen(false); goNew("/app/inspections"); }} />
                                    <QAItem icon={HardHat} label="New CDM audit" testId="qa-cdm-audit" onClick={() => { setQuickOpen(false); setRecordPreset("PR-CDM-SITE-INSPECTION"); setRecordDialog("inspection"); }} />
                                    <QAItem icon={ShieldCheck} label="SHE Audit" testId="qa-she-audit" onClick={() => { setQuickOpen(false); setRecordPreset("SHE-AUDIT"); setRecordDialog("inspection"); }} />
                                    <QAItem icon={BadgeCheck} label="QA Inspection" testId="qa-qa-inspection" onClick={() => { setQuickOpen(false); setRecordPreset("QA-INSP"); setRecordDialog("inspection"); }} />
                                    <QAItem icon={HardHat} label="Scaffold Inspection" testId="qa-scaffold-inspection" onClick={() => { setQuickOpen(false); setRecordPreset("SCAF-INSP"); setRecordDialog("inspection"); }} />
                                    <QAItem icon={Upload} label="Upload document" testId="qa-document" onClick={() => { setQuickOpen(false); goNew("/app/documents"); }} />
                                    <QAItem icon={ListPlus} label="Create action" testId="qa-action" onClick={() => { setQuickOpen(false); goNew("/app/actions"); }} />
                                    <QAItem icon={Truck} label="Construction Logistics Plan" testId="qa-clp" onClick={() => { setQuickOpen(false); openClp(); }} />
                                    <QAItem icon={Send} label="Send to QA" testId="qa-qa" onClick={() => { setQuickOpen(false); sendToQa(); }} />
                                    <QAItem icon={Globe2} label={project.published_to_portal ? "Unpublish" : "Publish to portal"} testId="qa-publish" onClick={() => { setQuickOpen(false); publish(); }} />
                                    <div className="my-1.5 border-t" style={{ borderColor: "#F0EDE8" }} />
                                    <QAItem icon={Archive} label="Archive project" testId="qa-archive" danger onClick={() => { setQuickOpen(false); archive(); }} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs: Overview / Compliance Hub / Contacts & Access + persistent Edit project */}
            <div className="flex items-center justify-between gap-3 flex-wrap border-b" style={{ borderColor: "#E5E0DA" }} data-testid="project-detail-tabs">
                <div className="flex items-center gap-1">
                    {[{ k: "overview", l: "Overview", Icon: LayoutDashboard }, { k: "compliance", l: "Compliance Hub", Icon: ShieldCheck }, { k: "documents", l: "Documents", Icon: FileText }, { k: "records", l: "Induction Records", Icon: HardHat }, { k: "rams", l: "RAMS", Icon: PencilRuler }, { k: "cpp", l: "CPP / PCI", Icon: Construction }, { k: "equipment", l: "Equipment on Site", Icon: Wrench }, { k: "people", l: "Contacts & Access", Icon: Users }, { k: "calendar", l: "Calendar", Icon: CalendarDays }].map((t) => (
                        <button key={t.k} data-testid={`project-tab-${t.k}`} onClick={() => setActiveTab(t.k)}
                            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-semibold rounded-t-[8px] border-b-2 -mb-px ${activeTab === t.k ? "border-[var(--brand-accent,#C75A1B)] text-[#1F1F1F]" : "border-transparent text-[#64718a] hover:text-[#3e4b63]"}`}>
                            <t.Icon className="w-4 h-4" style={{ color: activeTab === t.k ? "var(--brand-accent,#C75A1B)" : "#94a0b4" }} />
                            {t.l}
                        </button>
                    ))}
                </div>
                {canEdit && (
                    <div className="flex items-center gap-2 pb-1.5">
                        <button data-testid="project-edit-open" onClick={() => setEditOpen(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[8px] text-[11.5px] font-semibold border bg-white hover:bg-[#F6F4F0]"
                            style={{ borderColor: "#E5E0DA", color: "#1f2a44" }}>
                            <Pencil className="w-3.5 h-3.5" style={{ color: "var(--brand-accent,#C75A1B)" }} /> Edit project
                        </button>
                    </div>
                )}
            </div>

            {activeTab === "people" && (
                <div className="space-y-5" data-testid="project-people-tab">
                    <div className="bg-white border rounded-[13px] shadow-brand-sm p-5" style={{ borderColor: "#E5E0DA" }}>
                        <ProjectContactsEditor projectId={id} canEdit={canEdit} onChanged={() => setAccessRefreshKey((k) => k + 1)} />
                    </div>
                    {canEdit && (
                        <div className="bg-white border rounded-[13px] shadow-brand-sm p-5" style={{ borderColor: "#E5E0DA" }}>
                            <ProjectAccessPanel projectId={id} clientName={client?.name} refreshKey={accessRefreshKey}
                                onChanged={() => setAccessRefreshKey((k) => k + 1)} />
                        </div>
                    )}
                    {canEdit && (
                        <div className="bg-white border rounded-[13px] shadow-brand-sm p-5" style={{ borderColor: "#E5E0DA" }}>
                            <AccessHistoryPanel projectId={id} refreshKey={accessRefreshKey} />
                        </div>
                    )}
                </div>
            )}

            {activeTab === "documents" && (
                <div className="space-y-3" data-testid="project-documents-tab">
                    <DocumentBrowser clientId={project?.client_id} projectId={id} projectLabel={project?.name}
                        user={user} clients={client ? [client] : []} />
                </div>
            )}

            {activeTab === "records" && (
                <div className="space-y-3" data-testid="project-records-tab">
                    <ProjectInductionRecords projectId={id} />
                </div>
            )}

            {activeTab === "rams" && (
                <div className="space-y-3" data-testid="project-rams-tab">
                    <ProjectRamsPanel projectId={id} clientId={project?.client_id} />
                </div>
            )}

            {activeTab === "cpp" && (
                <div className="space-y-3" data-testid="project-cpp-tab">
                    <ProjectCppPanel projectId={id} clientId={project?.client_id} />
                </div>
            )}

            {activeTab === "equipment" && (
                <div className="space-y-3" data-testid="project-equipment-tab">
                    <div className="bg-white border rounded-[13px] shadow-brand-sm p-5" style={{ borderColor: "#E5E0DA" }}>
                        <EquipmentOnSitePanel clientId={project?.client_id} projectId={id} canEdit={canEdit} registerEnabled={!!client?.equipment_register_enabled} />
                    </div>
                </div>
            )}

            {activeTab === "calendar" && (
                <div className="space-y-3" data-testid="project-calendar-tab">
                    <UnifiedCalendar clientId={project?.client_id} projectId={id}
                        canEdit={canEdit} role={user?.role} />
                </div>
            )}

            {activeTab === "compliance" && (
                (compliance?.context?.is_internal === false)
                    ? <PortalComplianceHub compliance={compliance} project={project} backParam={backParam} />
                    : <div className="space-y-5">
                        <CdmRollup projectId={id} />
                        <ProjectTimeSummary projectId={id} />
                        <ComplianceHub compliance={compliance} project={project} canEdit={canEdit}
                            onAddRecord={(type) => setRecordDialog(type)} goNew={goNew} backParam={backParam}
                            onRefresh={load} onOpenDocuments={() => setActiveTab("documents")} />
                    </div>
            )}

            {activeTab === "overview" && (<div className="space-y-5" data-testid="project-overview-tab">
            <EndClientCard project={project} canEdit={canEdit} onSaved={load} />
            <ProjectNotesCard project={project} canEdit={canEdit} onSaved={load} />
            <ProjectModuleToggles project={project} canEdit={canEdit} onChanged={load} />
            <SiteQRPoster clientId={project.client_id} siteId={project.site_id || (project.site_ids || [])[0]} projectId={project.id} siteName={project.name} projectName={project.name} pickTemplate />
            <ProjectSitesPanel project={project} canEdit={canEdit} onChanged={load} />
            <div className="grid lg:grid-cols-2 gap-5">
                <ProjectSubcontractorsPanel project={project} canEdit={canEdit} onChanged={load} />
                <ProjectWorksPanel project={project} canEdit={canEdit} onChanged={load} />
            </div>
            <RequiredAuditsPanel project={project} canEdit={canEdit} />
            {/* Consolidated project status: overall progress + workflow stages + phase schedule */}
            <SectionCard testId="project-progress-card" id={`workflow-${project.id}`} icon={BadgeCheck} title="Project workflow"
                actions={(
                    <div className="flex items-center gap-1.5 text-[11px]" style={{ color: "#3e4b63" }}>
                        <TrendingUp className="w-3.5 h-3.5" style={{ color: "var(--brand-accent,#C75A1B)" }} />
                        <span className="font-mono text-[14px] font-bold text-[#1F1F1F]" data-testid="project-overall-progress">{project.progress_pct ?? 0}%</span>
                    </div>
                )}>
                <div className="h-2.5 rounded-full overflow-hidden mt-3" style={{ background: "#EFEAE3" }}>
                    <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${project.progress_pct ?? 0}%`, background: "var(--brand-accent,#C75A1B)" }} />
                </div>
                {project.health_reasons?.length > 0 && (
                    <div data-testid="project-health-reasons" className="text-[11.5px] mt-2 flex items-center gap-1.5" style={{ color: HEALTH[project.health]?.fg || "#3e4b63" }}>
                        <AlertTriangle className="w-3.5 h-3.5" /> {project.health_reasons.join(" · ")}
                    </div>
                )}
                {project.completion_blocked && (
                    <div data-testid="project-completion-blocked" className="text-[11.5px] mt-2 flex items-center gap-1.5 rounded-[8px] px-2.5 py-1.5" style={{ color: "#C68A1C", background: "#FCF6EA" }}>
                        <AlertTriangle className="w-3.5 h-3.5" /> Completion blocked — {(project.critical_documents_total - project.critical_documents_approved)} of {project.critical_documents_total} critical document(s) awaiting approval.
                    </div>
                )}

                {/* Project status metrics */}
                <div data-testid="project-status-strip" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-4">
                    <StatTile label="Current stage" testId="stat-current-stage" value={stages.find((s) => s.key === project.workflow_stage)?.label || "—"} />
                    <StatTile label="RAG status" testId="stat-rag" value={HEALTH[project.health]?.label || "—"}
                        tone={project.health} />
                    <StatTile label="Overdue tasks" testId="stat-overdue-tasks" value={(related?.summary?.overdue_tasks ?? project.overdue_tasks) || 0}
                        tone={((related?.summary?.overdue_tasks ?? project.overdue_tasks) || 0) > 0 ? "red" : null} />
                    <StatTile label="Outstanding client actions" testId="stat-client-actions" value={related?.summary?.open_client_actions ?? "—"} />
                    <StatTile label="Outstanding PR actions" testId="stat-pr-actions" value={related?.summary?.open_pr_actions ?? "—"} />
                    <StatTile label="Missing documents" testId="stat-missing-docs" value={(related?.summary?.missing_documents ?? project.missing_documents) || 0}
                        tone={((related?.summary?.missing_documents ?? project.missing_documents) || 0) > 0 ? "red" : null} />
                </div>

                <div className="flex items-stretch gap-1 overflow-x-auto pb-2 mt-4" data-testid="project-stepper">
                    {stages.map((s, i) => {
                        const isActive = s.key === activeStage;
                        const isCurrent = s.key === project.workflow_stage;
                        return (
                            <button key={s.key} onClick={() => setActiveStage(s.key)} data-testid={`stage-node-${s.key}`}
                                className={`flex-1 min-w-[120px] text-left rounded-[9px] px-3 py-2.5 border transition-colors ${isActive ? "border-[var(--brand-accent,#C75A1B)] bg-[#FBEFE5]" : "border-transparent hover:bg-[#F6F4F0]"}`}>
                                <div className="flex items-center gap-1.5" style={{ color: stageColor(s.status) }}>
                                    {stageIcon(s.status)}
                                    <span className="text-[10px] font-bold uppercase tracking-wide">{`0${i + 1}`.slice(-2)}</span>
                                    {isCurrent && <span className="ml-auto text-[8.5px] font-bold uppercase px-1.5 py-0.5 rounded-full text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>Now</span>}
                                </div>
                                <div className="text-[11.5px] font-semibold text-[#1F1F1F] mt-1 leading-tight">{s.label}</div>
                                <div className="text-[10px] mt-0.5 font-medium flex items-center justify-between" style={{ color: stageColor(s.status) }}>
                                    <span>{s.status}</span>
                                    <span className="font-mono">{(project.phase_progress || {})[s.key] ?? 0}%</span>
                                </div>
                            </button>
                        );
                    })}
                </div>
                <div className="mt-4 pt-4 border-t" style={{ borderColor: "#F0EDE8" }}>
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#64718a] mb-3 flex items-center gap-1.5">
                        <CalendarDays className="w-3.5 h-3.5" /> Phase schedule
                    </div>
                    <GanttTimeline stages={stages} phaseProgress={project.phase_progress || {}}
                        editable={canEdit}
                        onPhaseChange={async (stageKey, dates) => { if (await patchStage(stageKey, dates)) toast.success("Phase rescheduled"); }} />
                </div>
            </SectionCard>

            {/* Selected stage panel */}
            {stage && (
                <div className="bg-white border rounded-[13px] shadow-brand-sm p-5" style={{ borderColor: "#E5E0DA" }} data-testid="stage-panel">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <div>
                            <h3 className="text-[15px] font-bold text-[#1F1F1F]">{stage.label}</h3>
                            <div className="flex items-center gap-3 mt-1 text-[11px]" style={{ color: "#64718a" }}>
                                {stage.owner && <span>Owner: {stage.owner}</span>}
                                {(stage.planned_start || stage.planned_end) && (
                                    <span data-testid="stage-planned-dates">Scheduled: {(stage.planned_start || "?").slice(0, 10)} → {(stage.planned_end || "?").slice(0, 10)}</span>
                                )}
                                {stage.started_at && <span>Started {stage.started_at.slice(0, 10)}</span>}
                                {stage.completed_at && <span>Completed {stage.completed_at.slice(0, 10)}</span>}
                            </div>
                        </div>
                        <Pill tone={STAGE_TONE[stage.status]}>{stage.status}</Pill>
                    </div>

                    {canEdit && (
                        <div className="grid sm:grid-cols-2 gap-3 mt-4">
                            <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#3e4b63" }}>Stage status</span>
                                <select data-testid="stage-status-select" value={stage.status} disabled={busy}
                                    onChange={(e) => patchStage(stage.key, { status: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none" style={{ borderColor: "#E5E0DA" }}>
                                    {STAGE_STATUSES.map((s) => <option key={s}>{s}</option>)}
                                </select>
                            </label>
                            <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#3e4b63" }}>Owner</span>
                                <input data-testid="stage-owner-input" defaultValue={stage.owner || ""} disabled={busy}
                                    onBlur={(e) => { if (e.target.value !== (stage.owner || "")) patchStage(stage.key, { owner: e.target.value }); }}
                                    placeholder="Responsible person" className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none" style={{ borderColor: "#E5E0DA" }} />
                            </label>
                            <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#3e4b63" }}>Planned start</span>
                                <input data-testid="stage-start-input" type="date" value={(stage.planned_start || "").slice(0, 10)} disabled={busy}
                                    onChange={(e) => patchStage(stage.key, { planned_start: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none" style={{ borderColor: "#E5E0DA" }} />
                            </label>
                            <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#3e4b63" }}>Planned end</span>
                                <input data-testid="stage-end-input" type="date" value={(stage.planned_end || "").slice(0, 10)} disabled={busy}
                                    min={(stage.planned_start || "").slice(0, 10) || undefined}
                                    onChange={(e) => patchStage(stage.key, { planned_end: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none" style={{ borderColor: "#E5E0DA" }} />
                            </label>
                            <label className="block sm:col-span-2"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#3e4b63" }}>Notes</span>
                                <textarea data-testid="stage-notes-input" rows={2} defaultValue={stage.notes || ""} disabled={busy}
                                    onBlur={(e) => { if (e.target.value !== (stage.notes || "")) patchStage(stage.key, { notes: e.target.value }); }}
                                    className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none" style={{ borderColor: "#E5E0DA" }} />
                            </label>
                        </div>
                    )}

                    {/* Tasks */}
                    <div className="mt-4">
                        <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#64718a] mb-2">Tasks &amp; required items</div>
                        <div className="space-y-1.5">
                            {(stage.checklist || []).map((ci) => {
                                const act = ci.action_id ? actionsById[ci.action_id] : null;
                                const due = act ? dueInfo(act) : null;
                                const slug = ci.label.replace(/\s+/g, "-").toLowerCase();
                                return (
                                <div key={ci.id || ci.label} data-testid={`task-row-${stage.key}-${slug}`}
                                    className={`px-3 py-2 rounded-lg border ${ci.done ? "bg-[#EEF6F0] border-[#CDE6D5]" : "bg-white border-[#E5E0DA]"}`}>
                                    <div className="flex items-center gap-2.5">
                                        <button onClick={() => canEdit && toggleChecklist(stage.key, ci.label, !ci.done)} disabled={!canEdit || busy}
                                            data-testid={`checklist-${stage.key}-${slug}`}
                                            className={`flex items-center gap-2.5 flex-1 text-left min-w-0 ${canEdit ? "" : "cursor-default"}`}>
                                            {ci.done ? <CheckCircle2 className="w-4 h-4 text-[#2E7D4F] shrink-0" /> : <Circle className="w-4 h-4 text-[#B8B2A8] shrink-0" />}
                                            <span className={`text-[12.5px] truncate ${ci.done ? "text-[#2E7D4F] font-medium" : "text-[#2D2D2D]"}`}>{ci.label}</span>
                                        </button>
                                        {act ? (
                                            <span data-testid={`task-action-linked-${stage.key}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: "#EAF1FB", color: "#2A5DB0" }}><ClipboardCheck className="w-3 h-3" /> Action</span>
                                        ) : ci.action_id ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: "#EAF1FB", color: "#2A5DB0" }}><ClipboardCheck className="w-3 h-3" /> Action</span>
                                        ) : canEdit && (
                                            <button data-testid={`task-create-action-${stage.key}-${slug}`}
                                                onClick={() => setTaskAction({ stageKey: stage.key, task: ci })} disabled={busy}
                                                className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-1 rounded-md border shrink-0 hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#3e4b63" }}>
                                                <ListPlus className="w-3 h-3" /> Action
                                            </button>
                                        )}
                                        {canEdit && (
                                            <button data-testid={`task-remove-${stage.key}-${slug}`}
                                                onClick={() => removeTask(stage.key, ci)} disabled={busy}
                                                className="text-[#C2837A] hover:text-[#B33A2E] p-1 rounded shrink-0" title="Remove task"><Trash2 className="w-3.5 h-3.5" /></button>
                                        )}
                                    </div>

                                    {/* task detail row: status / owner / due / flags */}
                                    <div className="flex items-center flex-wrap gap-2 mt-1.5 pl-[26px]">
                                        {canEdit ? (
                                            <select data-testid={`task-status-${stage.key}-${slug}`} value={ci.status || (ci.done ? "Complete" : "Not Started")}
                                                onChange={(e) => patchTask(stage.key, ci.id, { status: e.target.value })} disabled={busy || !ci.id}
                                                className="text-[11px] px-2 py-1 border rounded-md outline-none bg-white" style={{ borderColor: "#E5E0DA" }}>
                                                {TASK_STATUSES.map((st) => <option key={st}>{st}</option>)}
                                            </select>
                                        ) : (
                                            <Pill tone={TASK_TONE[ci.status || (ci.done ? "Complete" : "Not Started")]}>{ci.status || (ci.done ? "Complete" : "Not Started")}</Pill>
                                        )}
                                        {canEdit ? (
                                            <input data-testid={`task-due-${stage.key}-${slug}`} type="date" value={(ci.due_date || "").slice(0, 10)} disabled={busy || !ci.id}
                                                onChange={(e) => patchTask(stage.key, ci.id, { due_date: e.target.value })}
                                                className="text-[11px] px-2 py-1 border rounded-md outline-none bg-white" style={{ borderColor: "#E5E0DA" }} title="Due date" />
                                        ) : ci.due_date ? (
                                            <span className="text-[11px] inline-flex items-center gap-1" style={{ color: "#3e4b63" }}><CalendarDays className="w-3 h-3" />{ci.due_date.slice(0, 10)}</span>
                                        ) : null}
                                        {ci.owner && <span className="text-[11px] inline-flex items-center gap-1" style={{ color: "#64718a" }}><User className="w-3 h-3" />{ci.owner}</span>}
                                        {ci.required_document && <Pill tone="amber">Req doc</Pill>}
                                        {ci.evidence_required && <Pill tone="blue">Evidence</Pill>}
                                        {!ci.client_visible && <span data-testid={`task-internal-${stage.key}-${slug}`} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "#F0EDE8", color: "#64718a" }}>Internal</span>}
                                    </div>
                                    {ci.description && <div className="mt-1 pl-[26px] text-[11px]" style={{ color: "#3e4b63" }}>{ci.description}</div>}

                                    {act && (
                                        <div data-testid={`task-action-meta-${stage.key}-${slug}`} className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5 pl-[26px] text-[11px]" style={{ color: "#3e4b63" }}>
                                            <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{act.assignee_name || "Unassigned"}{act.assignee_email ? ` · ${act.assignee_email}` : ""}</span>
                                            {act.due_date && <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" />{act.due_date}</span>}
                                            <span data-testid={`task-action-due-${stage.key}-${slug}`} className="font-bold px-1.5 py-0.5 rounded" style={{ background: due.bg, color: due.fg }}>{due.label}</span>
                                        </div>
                                    )}
                                    {ci.action_id && !act && (
                                        <div className="mt-1.5 pl-[26px] text-[11px] italic" style={{ color: "#64718a" }}>Linked action (details loading…)</div>
                                    )}
                                </div>
                            );})}
                            {(stage.checklist || []).length === 0 && <div className="text-[12px] italic text-[#64718a]">No tasks yet.</div>}
                        </div>
                        {canEdit && (
                            <div className="flex items-center gap-2 mt-2.5">
                                <input data-testid={`task-add-input-${stage.key}`} value={newTask} onChange={(e) => setNewTask(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") addTask(stage.key); }} placeholder="Add a task…"
                                    className="flex-1 px-3 py-2 border rounded-lg text-[12.5px] outline-none" style={{ borderColor: "#E5E0DA" }} />
                                <button data-testid={`task-add-button-${stage.key}`} onClick={() => addTask(stage.key)} disabled={busy || !newTask.trim()}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[8px] text-[12px] font-semibold border bg-white disabled:opacity-50" style={{ borderColor: "#E5E0DA" }}>
                                    <Plus className="w-3.5 h-3.5" /> Add
                                </button>
                            </div>
                        )}
                    </div>

                    {canEdit && curIdx < stages.length - 1 && (
                        <button onClick={advance} disabled={busy} data-testid="stage-advance-button"
                            className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            Advance to next stage
                        </button>
                    )}
                </div>
            )}

            </div>)}

            {taskAction && (
                <TaskActionDialog projectId={id} stageKey={taskAction.stageKey} task={taskAction.task}
                    suggestions={[project.lead_consultant, stage?.owner].filter(Boolean)}
                    onClose={() => setTaskAction(null)}
                    onCreated={(data) => { setProject(data.project); setTaskAction(null); toast.success("Action created & assigned"); load(); }} />
            )}
            {recordDialog && (
                <AddRecordDialog type={recordDialog} project={project} phase={stage}
                    suggestions={[project.lead_consultant, stage?.owner].filter(Boolean)}
                    presetTemplateCode={recordPreset}
                    onClose={() => { setRecordDialog(null); setRecordPreset(""); }}
                    onCreated={(created) => {
                        const t = recordDialog;
                        setRecordDialog(null); setRecordPreset("");
                        if (t === "inspection" && created?.id) {
                            navigate(`/app/inspections/${created.id}?back=/app/projects/${id}`);
                            return;
                        }
                        toast.success("Record added to this phase"); load();
                    }} />
            )}
            <ProjectDialog open={editOpen} mode="edit" initial={project} clients={client ? [client] : []}
                onClose={() => setEditOpen(false)} onSaved={load} />
        </div>
    );
}

function StatTile({ label, value, testId, tone }) {
    const toneColor = { red: "#B33A2E", amber: "#C68A1C", green: "#3F7D3A" }[tone];
    return (
        <div data-testid={testId} className="rounded-[9px] border px-2.5 py-2" style={{ borderColor: "#EFEAE3", background: "#FCFBF9" }}>
            <div className="text-[9px] font-bold uppercase tracking-wide truncate" style={{ color: "#64718a" }}>{label}</div>
            <div className="text-[13px] font-bold mt-0.5 truncate" style={{ color: toneColor || "#1F1F1F" }} title={String(value)}>{value}</div>
        </div>
    );
}

function EndClientCard({ project, canEdit, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(project.end_client || "");
    const [busy, setBusy] = useState(false);
    async function save() {
        setBusy(true);
        try {
            await api.patch(`/projects/${project.id}`, { end_client: val.trim() || null });
            toast.success("End client updated");
            setEditing(false); onSaved?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
        finally { setBusy(false); }
    }
    return (
        <div className="bg-white border rounded-[13px] shadow-brand-sm px-5 py-3.5 flex items-center gap-3 flex-wrap" style={{ borderColor: "#E5E0DA" }} data-testid="project-end-client-card">
            <User className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} />
            <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "#64718a" }}>End client (employer)</span>
            {editing ? (
                <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                    <input data-testid="end-client-input" value={val} onChange={(e) => setVal(e.target.value)} placeholder="e.g. RHP / Airey Miller"
                        className="flex-1 px-2.5 py-1.5 border rounded-lg text-[12.5px] outline-none" style={{ borderColor: "#E5E0DA" }} />
                    <button data-testid="end-client-save" onClick={save} disabled={busy}
                        className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>{busy ? "…" : "Save"}</button>
                    <button onClick={() => { setEditing(false); setVal(project.end_client || ""); }} className="px-2.5 py-1.5 rounded-lg text-[12px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                </div>
            ) : (
                <>
                    <span data-testid="end-client-value" className="text-[13px] font-semibold text-[#1F1F1F]">{project.end_client || <span className="italic font-normal" style={{ color: "#9A968F" }}>Not set</span>}</span>
                    {canEdit && <button data-testid="end-client-edit" onClick={() => setEditing(true)} className="ml-auto inline-flex items-center gap-1 text-[11.5px] font-semibold" style={{ color: "var(--brand-accent,#C75A1B)" }}><Pencil className="w-3.5 h-3.5" /> Edit</button>}
                </>
            )}
        </div>
    );
}


function ProjectNotesCard({ project, canEdit, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(project.notes || "");
    const [busy, setBusy] = useState(false);
    async function save() {
        setBusy(true);
        try {
            await api.patch(`/projects/${project.id}`, { notes: val.trim() || null });
            toast.success("Notes updated");
            setEditing(false); onSaved?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
        finally { setBusy(false); }
    }
    return (
        <div className="bg-white border rounded-[13px] shadow-brand-sm px-5 py-3.5" style={{ borderColor: "#E5E0DA" }} data-testid="project-notes-card">
            <div className="flex items-center gap-3 flex-wrap">
                <FileText className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} />
                <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "#64718a" }}>Project notes</span>
                {!editing && canEdit && (
                    <button data-testid="project-notes-edit" onClick={() => setEditing(true)} className="ml-auto inline-flex items-center gap-1 text-[11.5px] font-semibold" style={{ color: "var(--brand-accent,#C75A1B)" }}><Pencil className="w-3.5 h-3.5" /> Edit</button>
                )}
            </div>
            {editing ? (
                <div className="mt-2.5 space-y-2">
                    <textarea data-testid="project-notes-input" rows={4} value={val} onChange={(e) => setVal(e.target.value)}
                        placeholder="General notes about this project — background, agreements, anything worth flagging for the team…"
                        className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none focus:border-[var(--brand-accent,#C75A1B)]" style={{ borderColor: "#E5E0DA" }} />
                    <div className="flex items-center gap-2">
                        <button data-testid="project-notes-save" onClick={save} disabled={busy}
                            className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>{busy ? "…" : "Save"}</button>
                        <button onClick={() => { setEditing(false); setVal(project.notes || ""); }} className="px-2.5 py-1.5 rounded-lg text-[12px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    </div>
                </div>
            ) : (
                <p data-testid="project-notes-value" className="text-[12.5px] mt-2 whitespace-pre-wrap" style={{ color: project.notes ? "#2D2D2D" : "#9A968F" }}>
                    {project.notes || <span className="italic">No notes yet.</span>}
                </p>
            )}
        </div>
    );
}


function TaskActionDialog({ projectId, stageKey, task, suggestions = [], onClose, onCreated }) {
    const [form, setForm] = useState({ assignee_name: "", assignee_email: "", assignee_type: "internal", due_date: "", priority: "Medium" });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function submit() {
        if (!form.assignee_name.trim()) { toast.error("Who should this be assigned to?"); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/projects/${projectId}/stages/${stageKey}/tasks/create-action`, {
                task_id: task.id, label: task.label,
                assignee_name: form.assignee_name.trim(),
                assignee_email: form.assignee_email.trim() || null,
                assignee_type: form.assignee_type,
                due_date: form.due_date || null, priority: form.priority,
            });
            onCreated(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create action"); }
        finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="task-action-dialog" className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[460px] border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-5 pt-4 pb-3 border-b flex items-center justify-between" style={{ borderColor: "#F0EDE8" }}>
                    <h3 className="font-display text-[16px] font-bold text-[#1F1F1F]">Assign task as an action</h3>
                    <button data-testid="task-action-close" onClick={onClose} className="w-8 h-8 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-5 py-4 space-y-3">
                    <div className="text-[12px] px-3 py-2 rounded-[8px]" style={{ background: "#FCFBF9", color: "#3e4b63" }}>{task.label}</div>
                    <div className="flex gap-2">
                        {["internal", "external"].map((t) => (
                            <button key={t} data-testid={`task-action-type-${t}`} onClick={() => set("assignee_type", t)}
                                className={`flex-1 px-3 py-2 rounded-[8px] text-[12px] font-semibold border capitalize ${form.assignee_type === t ? "text-white border-transparent" : "bg-white text-[#2D2D2D]"}`}
                                style={form.assignee_type === t ? { background: "var(--brand-accent,#C75A1B)" } : { borderColor: "#E5E0DA" }}>{t}</button>
                        ))}
                    </div>
                    <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#3e4b63" }}>Assign to</span>
                        <input data-testid="task-action-assignee" list="task-action-suggest" value={form.assignee_name} onChange={(e) => set("assignee_name", e.target.value)}
                            placeholder={form.assignee_type === "internal" ? "Priority Risk team member" : "Client / contractor contact"} className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none" style={{ borderColor: "#E5E0DA" }} />
                        <datalist id="task-action-suggest">{Array.from(new Set(suggestions)).map((s) => <option key={s} value={s} />)}</datalist>
                    </label>
                    {form.assignee_type === "external" && (
                        <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#3e4b63" }}>Email (optional)</span>
                            <input data-testid="task-action-email" type="email" value={form.assignee_email} onChange={(e) => set("assignee_email", e.target.value)} placeholder="name@company.co.uk" className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none" style={{ borderColor: "#E5E0DA" }} /></label>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#3e4b63" }}>Due date</span>
                            <input data-testid="task-action-due" type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none" style={{ borderColor: "#E5E0DA" }} /></label>
                        <label className="block"><span className="text-[11px] font-semibold mb-1 block" style={{ color: "#3e4b63" }}>Priority</span>
                            <select data-testid="task-action-priority" value={form.priority} onChange={(e) => set("priority", e.target.value)} className="w-full px-3 py-2 border rounded-lg text-[12.5px] outline-none" style={{ borderColor: "#E5E0DA" }}>
                                {["Low", "Medium", "High", "Critical"].map((p) => <option key={p}>{p}</option>)}
                            </select></label>
                    </div>
                </div>
                <div className="px-5 py-3.5 border-t flex items-center justify-end gap-2.5" style={{ borderColor: "#F0EDE8" }}>
                    <button onClick={onClose} disabled={busy} className="px-3.5 py-2 rounded-[8px] text-[12px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button data-testid="task-action-submit" onClick={submit} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardCheck className="w-3.5 h-3.5" />}Create action</button>
                </div>
            </div>
        </div>
    );
}

const ROLE_LABEL = {
    client_admin: "Client Admin", site_manager: "Site Manager", reviewer: "Reviewer",
};

function ProjectAccessPanel({ projectId, clientName, refreshKey, onChanged }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [inviting, setInviting] = useState(null);      // contact_id being invited
    const [inviteRole, setInviteRole] = useState("site_manager");
    const [inviteResult, setInviteResult] = useState(null);  // { name, email, role, temp_password }
    const [selected, setSelected] = useState(() => new Set());

    const load = useCallback(() => {
        api.get(`/projects/${projectId}/access`).then((r) => setData(r.data)).catch(() => setData({ internal_team: [], client_people: [] }));
    }, [projectId]);
    useEffect(() => { load(); }, [load, refreshKey]);

    const internalTeam = data?.internal_team || [];
    const clientPeople = data?.client_people || [];
    const selectableContacts = clientPeople.filter((c) => !c.all_projects);

    // Re-fetches the roster right before toggling so a grant made elsewhere
    // (e.g. the "Access required" checkbox in Project contacts) is never
    // silently wiped out by submitting a stale snapshot.
    async function applyToggle(kind, id) {
        setBusy(true);
        try {
            const { data: fresh } = await api.get(`/projects/${projectId}/access`);
            let internalIds = (fresh.internal_team || []).filter((u) => u.assigned).map((u) => u.id);
            let contactIds = (fresh.client_people || []).filter((c) => c.assigned && !c.all_projects).map((c) => c.contact_id);
            if (kind === "internal") {
                internalIds = internalIds.includes(id) ? internalIds.filter((x) => x !== id) : [...internalIds, id];
            } else {
                contactIds = contactIds.includes(id) ? contactIds.filter((x) => x !== id) : [...contactIds, id];
            }
            const { data: res } = await api.put(`/projects/${projectId}/access`, {
                internal_user_ids: internalIds, contact_ids: contactIds,
            });
            setData(res);
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update access"); }
        finally { setBusy(false); }
    }

    // Bulk grant/revoke for a whole selection of client contacts at once —
    // same freshness-before-write guard as applyToggle.
    async function bulkApply(action) {
        if (selected.size === 0) return;
        setBusy(true);
        try {
            const { data: fresh } = await api.get(`/projects/${projectId}/access`);
            const internalIds = (fresh.internal_team || []).filter((u) => u.assigned).map((u) => u.id);
            let contactIds = new Set((fresh.client_people || []).filter((c) => c.assigned && !c.all_projects).map((c) => c.contact_id));
            if (action === "grant") selected.forEach((cid) => contactIds.add(cid));
            else selected.forEach((cid) => contactIds.delete(cid));
            const { data: res } = await api.put(`/projects/${projectId}/access`, {
                internal_user_ids: internalIds, contact_ids: [...contactIds],
            });
            setData(res);
            setSelected(new Set());
            onChanged?.();
            toast.success(action === "grant" ? "Access granted to selected people" : "Access revoked for selected people");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update access"); }
        finally { setBusy(false); }
    }

    function toggleSelect(contactId) {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(contactId) ? next.delete(contactId) : next.add(contactId);
            return next;
        });
    }
    function toggleSelectAll() {
        setSelected((prev) => (prev.size === selectableContacts.length
            ? new Set()
            : new Set(selectableContacts.map((c) => c.contact_id))));
    }

    async function sendInvite(contact) {
        setBusy(true);
        try {
            const { data: res } = await api.post(`/projects/${projectId}/access/invite`, {
                contact_id: contact.contact_id, role: inviteRole,
            });
            const { temp_password, email, name, role, ...roster } = res;
            setData(roster);
            setInviteResult({ name, email, role, temp_password });
            setInviting(null);
            onChanged?.();
            toast.success(`${name} invited as a portal user`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send invite"); }
        finally { setBusy(false); }
    }

    const toggleInternal = (u) => applyToggle("internal", u.id);
    const toggleContact = (c) => { if (c.all_projects) return; applyToggle("contact", c.contact_id); };

    return (
        <div data-testid="project-access-panel">
            <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} />
                <h3 className="text-[12.5px] font-bold text-[#1F1F1F]">Project access</h3>
            </div>
            <p className="text-[11px] mb-3" style={{ color: "#64718a" }}>Tag the internal team and manage which client people can see this project.</p>
            <div className="space-y-5">
                    {inviteResult && (
                        <div data-testid="invite-result" className="rounded-[10px] border p-3" style={{ borderColor: "#CDE3C8", background: "#F1F8EF" }}>
                            <div className="text-[12px] font-bold text-[#2F6B2A] mb-1">Portal login created for {inviteResult.name}</div>
                            <div className="text-[11.5px] mb-2" style={{ color: "#3F6B3A" }}>Share these one-time credentials so they can sign in to the portal.</div>
                            <div className="flex items-center gap-2 text-[11.5px]">
                                <span className="px-2 py-1 rounded bg-white border font-mono" style={{ borderColor: "#CDE3C8" }}>{inviteResult.email}</span>
                                <span data-testid="invite-temp-password" className="px-2 py-1 rounded bg-white border font-mono font-semibold" style={{ borderColor: "#CDE3C8" }}>{inviteResult.temp_password}</span>
                                <button data-testid="invite-copy" onClick={() => { navigator.clipboard?.writeText(`${inviteResult.email} / ${inviteResult.temp_password}`); toast.success("Credentials copied"); }}
                                    className="ml-auto text-[11px] font-semibold px-2 py-1 rounded-md border hover:bg-white" style={{ borderColor: "#CDE3C8", color: "#2F6B2A" }}>Copy</button>
                                <button data-testid="invite-result-dismiss" onClick={() => setInviteResult(null)} className="text-[11px] font-semibold px-2 py-1 rounded-md hover:bg-white" style={{ color: "#3e4b63" }}>Dismiss</button>
                            </div>
                        </div>
                    )}
                    {data == null ? (
                        <div className="text-[11.5px] italic text-[#64718a] py-2"><Loader2 className="inline w-3 h-3 animate-spin mr-1" />Loading…</div>
                    ) : (
                        <>
                            {/* Internal team */}
                            <div data-testid="project-access-internal">
                                <div className="flex items-center gap-2 mb-1">
                                    <ShieldCheck className="w-3.5 h-3.5" style={{ color: "#3F7D3A" }} />
                                    <div className="text-[10.5px] font-bold uppercase tracking-wide" style={{ color: "#64718a" }}>Internal team</div>
                                </div>
                                <p className="text-[11px] mb-2" style={{ color: "#64718a" }}>Priority Risk staff retain full access — tag who is on this project team.</p>
                                <ul className="space-y-1.5">
                                    {internalTeam.map((u) => (
                                        <li key={u.id} data-testid={`project-access-internal-${u.id}`} className="flex items-center gap-3 px-3 py-2 rounded-[9px] border" style={{ borderColor: "#F0EDE8", background: u.assigned ? "#FCFBF9" : "#fff" }}>
                                            <Avatar name={u.name} />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{u.name}</div>
                                                <div className="text-[11px] truncate" style={{ color: "#64718a" }}>{ROLE_LABEL[u.role] || u.role}{u.title ? ` · ${u.title}` : ""}</div>
                                            </div>
                                            <AccessToggle on={u.assigned} busy={busy} onClick={() => toggleInternal(u)} testId={`project-access-internal-toggle-${u.id}`} onLabel="On team" offLabel="Add to team" />
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Client people */}
                            <div data-testid="project-access-client">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <div className="flex items-center gap-2">
                                        <Users className="w-3.5 h-3.5" style={{ color: "#2A5DB0" }} />
                                        <div className="text-[10.5px] font-bold uppercase tracking-wide" style={{ color: "#64718a" }}>{clientName || "Client"} people</div>
                                    </div>
                                    {selectableContacts.length > 0 && (
                                        <label className="flex items-center gap-1.5 text-[10.5px] font-semibold cursor-pointer" style={{ color: "#64718a" }}>
                                            <input data-testid="project-access-select-all" type="checkbox"
                                                checked={selected.size > 0 && selected.size === selectableContacts.length}
                                                onChange={toggleSelectAll} />
                                            Select all
                                        </label>
                                    )}
                                </div>
                                <p className="text-[11px] mb-2" style={{ color: "#64718a" }}>Client contacts who can see this project. Those with a portal login get gated access; Client Admins always see every project.</p>
                                {selected.size > 0 && (
                                    <div data-testid="project-access-bulk-bar" className="flex items-center gap-2 mb-2 px-3 py-2 rounded-[9px]" style={{ background: "#FBF2E7", border: "1px solid #EFD9B8" }}>
                                        <span className="text-[11.5px] font-semibold" style={{ color: "#3e4b63" }}>{selected.size} selected</span>
                                        <button data-testid="project-access-bulk-grant" disabled={busy} onClick={() => bulkApply("grant")}
                                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                            <Plus className="w-3 h-3" /> Grant access
                                        </button>
                                        <button data-testid="project-access-bulk-revoke" disabled={busy} onClick={() => bulkApply("revoke")}
                                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border hover:bg-[#FBE8E5]" style={{ borderColor: "#E7C9C4", color: "#B33A2E" }}>
                                            <Trash2 className="w-3 h-3" /> Revoke access
                                        </button>
                                        <button data-testid="project-access-bulk-clear" onClick={() => setSelected(new Set())}
                                            className="ml-auto text-[11px] font-semibold px-1.5 py-1 rounded-md hover:bg-white" style={{ color: "#64718a" }}>Clear</button>
                                    </div>
                                )}
                                {clientPeople.length === 0 ? (
                                    <div className="text-[11.5px] italic text-[#64718a] py-1">No contacts for this client yet — add them in the Contacts module.</div>
                                ) : (
                                    <ul className="space-y-1.5">
                                        {clientPeople.map((c) => (
                                            <li key={c.contact_id} data-testid={`project-access-contact-${c.contact_id}`} className="flex items-center gap-3 px-3 py-2 rounded-[9px] border" style={{ borderColor: "#F0EDE8", background: c.assigned ? "#FCFBF9" : "#fff" }}>
                                                {!c.all_projects && (
                                                    <input data-testid={`project-access-contact-checkbox-${c.contact_id}`} type="checkbox"
                                                        checked={selected.has(c.contact_id)} onChange={() => toggleSelect(c.contact_id)}
                                                        className="flex-shrink-0" />
                                                )}
                                                <Avatar name={c.name} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate flex items-center gap-1.5">
                                                        {c.name}
                                                        {!c.has_login && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ background: "#F0EDE8", color: "#64718a" }} title="No portal login — listed as a stakeholder">No login</span>}
                                                    </div>
                                                    <div className="text-[11px] truncate" style={{ color: "#64718a" }}>{c.role || "Contact"}{c.email ? ` · ${c.email}` : ""}</div>
                                                </div>
                                                {c.all_projects ? (
                                                    <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-1 rounded-full flex-shrink-0" style={{ background: "#E8F1E6", color: "#3F7D3A" }}>
                                                        <ShieldCheck className="w-3 h-3" /> All projects
                                                    </span>
                                                ) : inviting === c.contact_id ? (
                                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                                        <select data-testid={`invite-role-${c.contact_id}`} value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                                                            className="text-[11px] rounded-md border px-1.5 py-1 bg-white" style={{ borderColor: "#E5E0DA" }}>
                                                            <option value="client_admin">Client Admin</option>
                                                            <option value="site_manager">Site Manager</option>
                                                            <option value="reviewer">Reviewer</option>
                                                        </select>
                                                        <button data-testid={`invite-send-${c.contact_id}`} disabled={busy} onClick={() => sendInvite(c)}
                                                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                                            <Send className="w-3 h-3" /> Send
                                                        </button>
                                                        <button onClick={() => setInviting(null)} className="text-[11px] font-semibold px-1.5 py-1 rounded-md hover:bg-[#F6F4F0]" style={{ color: "#3e4b63" }}>Cancel</button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                                        {!c.has_login && (
                                                            <button data-testid={`invite-open-${c.contact_id}`} disabled={busy}
                                                                onClick={() => { setInviteRole("site_manager"); setInviting(c.contact_id); }}
                                                                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1.5 rounded-md border hover:bg-[#F6F4F0]"
                                                                style={{ borderColor: "#E5E0DA", color: "var(--brand-accent,#C75A1B)" }}>
                                                                <Send className="w-3 h-3" /> Invite
                                                            </button>
                                                        )}
                                                        <AccessToggle on={c.assigned} busy={busy} onClick={() => toggleContact(c)} testId={`project-access-contact-toggle-${c.contact_id}`} onLabel="Has access" offLabel="Add" />
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
    );
}

function AccessHistoryPanel({ projectId, refreshKey }) {
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState(null);

    useEffect(() => {
        if (!open) return;
        api.get(`/projects/${projectId}/access/history`).then((r) => setRows(r.data)).catch(() => setRows([]));
    }, [open, projectId, refreshKey]);

    return (
        <div data-testid="project-access-history-panel">
            <button data-testid="project-access-history-toggle" onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-2 w-full text-left">
                <History className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} />
                <h3 className="text-[12.5px] font-bold text-[#1F1F1F]">Access history</h3>
                <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${open ? "rotate-180" : ""}`} style={{ color: "#94a0b4" }} />
            </button>
            {!open && <p className="text-[11px] mt-1" style={{ color: "#64718a" }}>Who was granted or revoked access, when, and by whom.</p>}
            {open && (
                rows === null ? (
                    <div className="text-[11.5px] italic text-[#64718a] py-2 mt-2"><Loader2 className="inline w-3 h-3 animate-spin mr-1" />Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="text-[11.5px] italic text-[#64718a] py-2 mt-2">No access changes recorded for this project yet.</div>
                ) : (
                    <ul className="space-y-1.5 mt-2.5">
                        {rows.map((r) => (
                            <li key={r.id} data-testid={`project-access-history-row-${r.id}`} className="px-3 py-2 rounded-[9px] border" style={{ borderColor: "#F0EDE8" }}>
                                <div className="text-[12px] text-[#1F1F1F]">{r.description}</div>
                                <div className="text-[10.5px] mt-0.5" style={{ color: "#94a0b4" }}>
                                    {new Date(r.created_at).toLocaleString("en-GB")}{r.actor_email ? ` · by ${r.actor_email}` : ""}
                                </div>
                            </li>
                        ))}
                    </ul>
                )
            )}
        </div>
    );
}


function AccessToggle({ on, busy, onClick, testId, onLabel, offLabel }) {
    return (
        <button data-testid={testId} disabled={busy} onClick={onClick}
            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border flex-shrink-0 ${on ? "hover:bg-[#FBE8E5]" : "text-white"}`}
            style={on ? { borderColor: "#E7C9C4", color: "#B33A2E" } : { background: "var(--brand-accent,#C75A1B)", borderColor: "transparent" }}>
            {on ? <Trash2 className="w-3 h-3" /> : <Plus className="w-3 h-3" />} {on ? onLabel : offLabel}
        </button>
    );
}

function Avatar({ name }) {
    return (
        <div className="w-8 h-8 rounded-full grid place-items-center text-[11px] font-bold flex-shrink-0" style={{ background: "#EFEAE3", color: "#3e4b63" }}>
            {(name || "?").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
        </div>
    );
}

function BuildingThumb() {
    // Decorative inline SVG building motif (no external asset).
    return (
        <div data-testid="project-thumbnail" aria-hidden="true"
            className="w-[72px] h-[88px] sm:w-20 sm:h-24 rounded-[12px] overflow-hidden flex-shrink-0 border" style={{ borderColor: "#EFE7DC" }}>
            <svg viewBox="0 0 80 96" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" role="img">
                <rect width="80" height="96" fill="#F3E9D9" />
                <rect x="8" y="34" width="14" height="62" fill="#E4D4BC" />
                <rect x="58" y="40" width="14" height="56" fill="#E4D4BC" />
                <rect x="26" y="16" width="28" height="80" fill="#1f2a44" />
                <rect x="30" y="10" width="20" height="8" rx="2" fill="#C75A1B" />
                <rect x="38" y="2" width="4" height="9" fill="#1f2a44" />
                {[24, 36, 48, 60, 72, 84].map((y) => (
                    [30, 37, 44].map((x) => (
                        <rect key={`${x}-${y}`} x={x} y={y} width="5" height="8" rx="1" fill="#3a4a6b" />
                    ))
                ))}
                <rect x="37" y="36" width="5" height="8" rx="1" fill="#E0922E" />
                <rect x="30" y="60" width="5" height="8" rx="1" fill="#E0922E" />
            </svg>
        </div>
    );
}

function QAItem({ icon: Icon, label, onClick, testId, danger }) {
    return (
        <button role="menuitem" onClick={onClick} data-testid={testId}
            className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-[12.5px] font-semibold text-left transition-colors ${danger ? "text-[#B33A2E] hover:bg-[#FBE8E5]" : "text-[#1f2a44] hover:bg-[#F6F4F0]"}`}>
            <Icon className="w-4 h-4" style={{ color: danger ? "#B33A2E" : "var(--brand-accent,#C75A1B)" }} /> {label}
        </button>
    );
}
